import type { StaffSession } from '@/api/types';

/**
 * Where the staff session lives.
 *
 * The access token is held in a module variable and NEVER written to storage.
 * It is short-lived and it is the credential that opens every admin route; a
 * copy in localStorage is a copy any injected script can read at leisure, and
 * the only thing it buys is not having to refresh after a reload.
 *
 * The refresh token IS in localStorage, deliberately. Without it, a dispatcher
 * loses their session on every F5 and a driver loses it every time the phone
 * backgrounds Safari hard enough to discard the tab — and the realistic outcome
 * of that is a shared password on a sticky note. The token rotates on every use
 * and the server treats a reused one as theft and revokes the family, so the
 * window a stolen copy is worth anything in is one round trip.
 *
 * sessionStorage would scope it to the tab, which sounds better and is worse:
 * the office opens orders in new tabs all day and each one would demand a login.
 *
 * Which makes localStorage the one shared truth and every tab a reader of it
 * rather than an owner. A tab that preferred its own copy would keep spending a
 * token another tab had already rotated away, and the server would be right to
 * read that as theft — that is the alarm the office learned to ignore. So reads
 * go to storage first, and a rotation elsewhere is adopted from the storage
 * event instead of only a sign-out being noticed.
 */

const REFRESH_TOKEN_KEY = 'dryclean.staff.refreshToken';

let current: StaffSession | null = null;

/**
 * Flipped by a write that threw. A tab that cannot write is not reading a value
 * anyone kept up to date either, so from then on its own memory is the only
 * copy of the truth it has.
 */
let storageWritable = true;

/** Absent and unreadable are different answers: only unreadable falls back to memory. */
type StoredRead = { readable: true; token: string | null } | { readable: false };

/** localStorage throws in a locked-down browser; never let that break sign-in. */
function readStored(): StoredRead {
  try {
    return { readable: true, token: window.localStorage.getItem(REFRESH_TOKEN_KEY) };
  } catch {
    return { readable: false };
  }
}

function writeStored(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    else window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {
    // A private window with storage denied still gets a working session, it
    // just ends when the tab does. That is a fine degradation.
    storageWritable = false;
  }
}

type RefreshTokenListener = (token: string | null) => void;

const listeners = new Set<RefreshTokenListener>();

/**
 * The other tabs' changes, arriving.
 *
 * A `storage` event fires only in the tabs that did not make the change, which
 * is exactly the set that needs telling, and it carries the new value. Both
 * halves matter: null is a sign-out, and anything else is the token that has
 * just replaced the one this tab was holding.
 */
function handleStorage(event: StorageEvent): void {
  if (event.key !== REFRESH_TOKEN_KEY) return;

  const token = event.newValue;
  if (token === null) current = null;
  else if (current !== null) current = { ...current, refreshToken: token };

  // Copied: a listener is allowed to unsubscribe itself while being told.
  for (const listener of [...listeners]) listener(token);
}

function subscribe(listener: RefreshTokenListener): () => void {
  if (listeners.size === 0) window.addEventListener('storage', handleStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', handleStorage);
  };
}

export const sessionStore = {
  get(): StaffSession | null {
    return current;
  },

  getAccessToken(): string | null {
    return current?.accessToken ?? null;
  },

  /**
   * Storage first, because storage is what the other tabs write to.
   *
   * The in-memory copy is this tab's memory of a rotation that may since have
   * been superseded, and presenting a superseded token is the reuse the server
   * kills the whole family for. Memory answers only for a tab whose storage is
   * denied, where it is the only answer there is.
   */
  getRefreshToken(): string | null {
    const stored = readStored();
    if (!stored.readable || !storageWritable) return current?.refreshToken ?? null;
    return stored.token;
  },

  set(session: StaffSession): void {
    current = session;
    writeStored(session.refreshToken);
  },

  clear(): void {
    current = null;
    writeStored(null);
  },

  /**
   * Sign-out in one tab should sign out the others.
   *
   * An office machine with the orders list open in four tabs must not leave
   * three of them live after someone signs out in the fourth.
   */
  subscribeToSignOut(handler: () => void): () => void {
    // Subscribing is also what attaches the listener that adopts a rotation, so
    // the provider's one subscription buys both.
    return subscribe((token) => {
      if (token === null) handler();
    });
  },
};
